// @flow
import { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import moment from 'moment';
import useIntl from '../../../hooks/useIntl';
import messages from '../messages';
import { MAX_OFFERS, getOfferExpirationDate } from './helpers';

export const DEFAULT_TRANSACTION_CODE = {
  id: 'default',
  transactionType: 'default',
  offer1: null,
  offer2: null,
  offer3: null,
};

// ENG-31380 — earliest acceptable expiration date, by state:
//   true  — TODAY, because a SAME_DAY config pre-fills today at midnight; rejecting it would
//           leave every SAME_DAY property un-submittable on its own pre-filled value.
//   false — TOMORROW, which must stay in step with ExpirationDateField's picker `minDate`. Change
//           one and you have to change the other.
export const makeResolver = (intl, isServerAuthoritative) => (values) => {
  const errors = {};

  // offerExpirationDate validation
  const { offerExpirationDate } = values;
  if (!offerExpirationDate || offerExpirationDate === 'MM/DD/YYYY') {
    errors.offerExpirationDate = intl.formatMessage(messages.mustSelectADate);
  } else {
    const formattedDate = moment(offerExpirationDate).startOf('day');
    const todayDate = moment(Date.now()).startOf('day');
    const earliestDate = isServerAuthoritative
      ? todayDate
      : moment(todayDate).add(1, 'day');
    if (!formattedDate.isValid()) {
      errors.offerExpirationDate = intl.formatMessage(
        messages.invalidDateFormat,
      );
    } else if (
      formattedDate.isBefore(earliestDate) ||
      formattedDate.isAfter(moment(todayDate).add(120, 'days'))
    ) {
      errors.offerExpirationDate = intl.formatMessage(
        messages.invalidDateValue,
      );
    }
  }

  // offer1Rent required
  if (!values.offer1Rent) {
    errors.offer1Rent = intl.formatMessage(messages.mustEnterALeaseRentAmount);
  }
  if (!values.offer1TermId) {
    errors.offer1TermId = intl.formatMessage(messages.mustChooseATermLength);
  }

  // Paired offer validation
  Array.from({ length: MAX_OFFERS }, (_, i) => i + 1).forEach((i) => {
    const length = values[`offer${i}TermId`];
    const rent = values[`offer${i}Rent`];
    if (!length && rent) {
      errors[`offer${i}TermId`] = intl.formatMessage(
        messages.mustChooseATermLength,
      );
    } else if (length && !rent) {
      errors[`offer${i}Rent`] = intl.formatMessage(
        messages.mustEnterALeaseRentAmount,
      );
    }
    if (rent && +rent <= 0) {
      errors[`offer${i}Rent`] = intl.formatMessage(
        messages.valueGreaterThanZero,
      );
    }
  });

  // each offer n>1 requires the previous offer
  for (let n = MAX_OFFERS; n >= 2; n--) {
    const nExists = values[`offer${n}TermId`] || values[`offer${n}Rent`];
    const prevExists =
      values[`offer${n - 1}TermId`] || values[`offer${n - 1}Rent`];
    if (nExists && !prevExists) {
      errors[`offer${n - 1}Rent`] = intl.formatMessage(
        messages.mustHaveOfferTwo,
      );
    }
  }

  // Transaction code validation
  const txErrors = (values.transactionCodes ?? []).map((codeValues) => {
    const txErr = {};
    if (codeValues.id !== 'default') {
      const totalOffersAmount = [
        codeValues.offer1,
        codeValues.offer2,
        codeValues.offer3,
      ].reduce((a, b) => Number(a) + Number(b), 0);
      if (totalOffersAmount <= 0) {
        txErr.id = intl.formatMessage(messages.mustHaveOfferAmount);
      }
      if (codeValues.offer1 && +codeValues.offer1 < 0) {
        txErr.offer1 = intl.formatMessage(messages.valuePositive);
      }
      if (codeValues.offer2 && +codeValues.offer2 < 0) {
        txErr.offer2 = intl.formatMessage(messages.valuePositive);
      }
      if (codeValues.offer3 && +codeValues.offer3 < 0) {
        txErr.offer3 = intl.formatMessage(messages.valuePositive);
      }
    }
    return txErr;
  });

  // Convert flat errors to RHF error shape
  const rhfErrors = {};
  Object.entries(errors).forEach(([field, message]) => {
    if (message) {
      rhfErrors[field] = { type: 'validate', message };
    }
  });

  const hasTransactionErrors = txErrors.some((e) => Object.keys(e).length > 0);
  if (hasTransactionErrors) {
    rhfErrors.transactionCodes = txErrors.map((e) => {
      const mapped = {};
      Object.entries(e).forEach(([k, v]) => {
        mapped[k] = { type: 'validate', message: v };
      });
      return mapped;
    });
  }

  const hasErrors = Object.keys(rhfErrors).length > 0;
  return {
    values: hasErrors ? {} : values,
    errors: rhfErrors,
  };
};

export function useRenewalOfferForm({
  onSubmit,
  quoteExpTime,
  isServerAuthoritative,
}) {
  const intl = useIntl();

  const methods = useForm({
    mode: 'onBlur',
    resolver: makeResolver(intl, isServerAuthoritative),
    defaultValues: {
      // Read ONCE by useForm; the effect below covers an input that settles later.
      offerExpirationDate: isServerAuthoritative
        ? getOfferExpirationDate(quoteExpTime)
        : 'MM/DD/YYYY',
      offer1Rent: '',
      offer1TermId: '',
      offer2Rent: '',
      offer2TermId: '',
      offer3Rent: '',
      offer3TermId: '',
      transactionCodes: [{ ...DEFAULT_TRANSACTION_CODE }],
    },
  });

  // ENG-31746 — both inputs can settle after useForm captured defaultValues, and no mount gate
  // covers either: quoteExpTime is ungated, and isPartnerEnabled comes from a leaseId-gated query.
  // Left alone, the field keeps the placeholder or a date derived from a null quoteExpTime.
  useEffect(() => {
    if (!isServerAuthoritative) return;
    // The server persists what the user picked, so never re-derive over their edit.
    if (methods.formState.dirtyFields.offerExpirationDate) return;
    methods.setValue(
      'offerExpirationDate',
      getOfferExpirationDate(quoteExpTime),
      { shouldValidate: true },
    );
  }, [isServerAuthoritative, quoteExpTime, methods]);

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'transactionCodes',
  });

  const addTransaction = () => {
    append({ ...DEFAULT_TRANSACTION_CODE });
  };

  const deleteTransaction = (index) => {
    remove(index);
  };

  const handleFormSubmit = methods.handleSubmit(async (values) => {
    try {
      await onSubmit(values);
      methods.reset();
    } catch {
      // onSubmit handles error display; keep form populated so user can retry
    }
  });

  return {
    methods,
    fields,
    addTransaction,
    deleteTransaction,
    handleFormSubmit,
  };
}
